import { io, Socket } from 'socket.io-client';

type LogUpdateCallback = (data: any) => void;
type ConnectionStateCallback = (state: ConnectionState) => void;
/** `import-job` payload: { id, status, error }. */
type ImportJobCallback = (data: { id: string; status: string; error: string | null }) => void;

export enum ConnectionState {
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  ERROR = 'error'
}

interface AuthenticatedSocket extends Socket {
  userId?: string;
}

interface ExecutionSubscription {
  executionId: string;
  callbacks: LogUpdateCallback[];
}

class LogsWebSocketService {
  private socket: AuthenticatedSocket | null = null;
  private url: string;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 3000;
  private subscriptions: Map<string, ExecutionSubscription> = new Map();
  private connectionState: ConnectionState = ConnectionState.DISCONNECTED;
  private stateCallbacks: ConnectionStateCallback[] = [];
  // kept across sockets: connect() binds them to every new one
  private importJobCallbacks: Set<ImportJobCallback> = new Set();
  private reconnectTimeout: NodeJS.Timeout | null = null;
  private apiBaseUrl: string;

  constructor(url?: string) {
    this.url = url || process.env.REACT_APP_WEBSOCKET_URL || 'http://localhost:3000';
    this.apiBaseUrl = process.env.REACT_APP_CORE_SERVICE_URL || 'http://localhost:3000';
  }

  connect(): void {
    // Existence guard, not `connected`: during the handshake the old check let a
    // second caller build a parallel io() socket and orphan the first one (still
    // open, still streaming log_update). socket.io reconnects on its own.
    if (this.socket) {
      return;
    }

    this.updateConnectionState(ConnectionState.CONNECTING);

    try {
      const token = localStorage.getItem('access_token');

      this.socket = io(`${this.url}/bot`, {
        auth: {
          token: token
        },
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.reconnectDelay
      });

      // Set up authenticated listener BEFORE connect to catch events that fire immediately
      this.socket.on('connected', (data: { userId?: string }) => {
        if (data?.userId && this.socket) {
          this.socket.userId = data.userId;
        } else {
          console.warn('WebSocket authenticated event received but no userId in data:', data);
        }
      });

      // this.socket.on('connect', this.handleOpen.bind(this));
      this.socket.on('subscribed', this.handleSubscribed.bind(this));
      this.socket.on('log_update', this.handleLogUpdate.bind(this));
      this.socket.on('import-job', (data: any) => {
        this.importJobCallbacks.forEach((cb) => {
          try {
            cb(data);
          } catch (error) {
            console.error('Error in import-job callback:', error);
          }
        });
      });
      // this.socket.on('execution_log', this.handleExecutionLog.bind(this));
      this.socket.on('error', this.handleServerError.bind(this));
      this.socket.on('connect_error', this.handleError.bind(this));
      this.socket.on('disconnect', this.handleClose.bind(this));

      // socket.io exhausted reconnectionAttempts: drop the dead socket so the
      // existence guard in connect() stops short-circuiting, then retry.
      this.socket.on('reconnect_failed', () => {
        this.teardownSocket();
        this.updateConnectionState(ConnectionState.ERROR);
        this.scheduleReconnect();
      });
    } catch (error) {
      console.error('Logs WebSocket connection error:', error);
      this.teardownSocket();
      this.updateConnectionState(ConnectionState.ERROR);
      this.scheduleReconnect();
    }
  }

  disconnect(): void {
    this.reconnectAttempts = this.maxReconnectAttempts;

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    this.teardownSocket();
    this.updateConnectionState(ConnectionState.DISCONNECTED);
  }

  async subscribeToExecutions(executionIds: string[], callback: LogUpdateCallback): Promise<() => void> {
    // Connect if not already connected
    if (!this.socket || !this.socket.connected) {
      this.connect();
    }

    // Wait for connection
    await new Promise<void>((resolve, reject) => {
      if (this.socket && this.socket.connected) {
        resolve();
        return;
      }

      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      const checkConnection = setInterval(() => {
        attempts++;
        if (this.socket && this.socket.connected) {
          clearInterval(checkConnection);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkConnection);
          reject(new Error('WebSocket connection timeout'));
        }
      }, 100);
    });

    // Wait for authentication (userId to be set)
    await new Promise<void>((resolve, reject) => {
      // Check if already authenticated
      if (this.socket?.userId) {
        resolve();
        return;
      }

      // Set up authenticated listener FIRST to catch events that fire quickly
      let resolved = false;
      const onAuthenticated = (data: { userId?: string }) => {
        if (resolved) return;
        if (data?.userId && this.socket) {
          this.socket.userId = data.userId;
          resolved = true;
          if (this.socket) {
            this.socket.off('authenticated', onAuthenticated);
          }
          resolve();
        }
      };

      if (this.socket) {
        this.socket.on('connected', onAuthenticated);
      }

      // Also poll in case the event already fired or userId is set another way
      let attempts = 0;
      const maxAttempts = 100; // 10 seconds max wait
      const checkAuth = setInterval(() => {
        if (resolved) {
          clearInterval(checkAuth);
          return;
        }

        attempts++;
        if (this.socket?.userId) {
          resolved = true;
          clearInterval(checkAuth);
          if (this.socket) {
            this.socket.off('authenticated', onAuthenticated);
          }
          resolve();
        } else if (attempts >= maxAttempts) {
          resolved = true;
          clearInterval(checkAuth);
          if (this.socket) {
            this.socket.off('connected', onAuthenticated);
          }
          reject(new Error('Authentication timeout - userId not set'));
        }
      }, 100);
    });

    // Add callbacks for each execution
    executionIds.forEach(executionId => {
      if (!this.subscriptions.has(executionId)) {
        this.subscriptions.set(executionId, {
          executionId,
          callbacks: []
        });
      }

      const subscription = this.subscriptions.get(executionId)!;
      subscription.callbacks.push(callback);
    });

    // Send subscription message to server (only if userId is set)
    if (this.socket && this.socket.connected && this.socket.userId) {
      this.socket.emit('subscribe_executions', { executionIds });
    } else {
      throw new Error('Cannot subscribe: userId not available');
    }

    // Return unsubscribe function
    return () => {
      this.unsubscribeFromExecutions(executionIds, callback);
    };
  }

  unsubscribeFromExecutions(executionIds: string[], callback: LogUpdateCallback): void {
    executionIds.forEach(executionId => {
      const subscription = this.subscriptions.get(executionId);
      if (!subscription) return;

      subscription.callbacks = subscription.callbacks.filter(cb => cb !== callback);

      if (subscription.callbacks.length === 0) {
        this.subscriptions.delete(executionId);
        // Note: Server only supports subscribe_executions, not unsubscribe_executions
      }
    });
  }

  /** Every `import-job` event sent to this user's room. Connects if needed. */
  onImportJob(callback: ImportJobCallback): () => void {
    this.importJobCallbacks.add(callback);
    this.connect();
    return () => {
      this.importJobCallbacks.delete(callback);
    };
  }

  /** Connected and authenticated, i.e. in the user's room and receiving its events. */
  isInUserRoom(): boolean {
    return !!this.socket?.connected && !!this.socket.userId;
  }

  onConnectionStateChange(callback: ConnectionStateCallback): () => void {
    this.stateCallbacks.push(callback);
    callback(this.connectionState);

    return () => {
      this.stateCallbacks = this.stateCallbacks.filter(cb => cb !== callback);
    };
  }

  getConnectionState(): ConnectionState {
    return this.connectionState;
  }

  isUserConnected(): boolean {
    return this.socket !== null && this.socket.connected;
  }

  getUserId(): string | undefined {
    return this.socket?.userId;
  }

  private handleOpen(): void {
    this.reconnectAttempts = 0;
    this.updateConnectionState(ConnectionState.CONNECTED);

    // Wait for authentication before resubscribing
    const resubscribe = () => {
      const allExecutionIds = Array.from(this.subscriptions.keys());
      if (allExecutionIds.length > 0 && this.socket && this.socket.userId) {
        this.socket.emit('subscribe_executions', { executionIds: allExecutionIds });
      }
    };

    // If already authenticated, resubscribe immediately
    if (this.socket?.userId) {
      resubscribe();
    } else {
      // Otherwise wait for authenticated event
      const onAuthenticated = (data: { userId?: string }) => {
        if (data?.userId && this.socket) {
          this.socket.userId = data.userId;
          resubscribe();
          if (this.socket) {
            this.socket.off('authenticated', onAuthenticated);
          }
        }
      };

      if (this.socket) {
        this.socket.once('authenticated', onAuthenticated);
      }
    }
  }

  private handleSubscribed(data: { success?: string[]; errors?: Array<{ executionId: string; error: string }> }): void {
    try {
      if (data.errors && data.errors.length > 0) {
        console.warn('Subscription errors:', data.errors);
        data.errors.forEach((err) => {
          console.error(`Failed to subscribe to execution ${err.executionId}: ${err.error}`);
        });
      }
    } catch (error) {
      console.error('Error handling subscribed event:', error);
    }
  }

  private handleServerError(data: { message?: string }): void {
    try {
      const errorMessage = data?.message || 'Server error';
      console.error('Server error:', errorMessage);
      // You can emit a custom event or use a callback here if needed
    } catch (error) {
      console.error('Error handling server error:', error);
    }
  }

  private handleLogUpdate(data: any): void {
    try {
      // Handle general log updates
      if (data && data.executionId) {
        const subscription = this.subscriptions.get(data.executionId);
        if (subscription) {
          subscription.callbacks.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in log subscription callback:', error);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error handling log update:', error);
    }
  }

  private handleExecutionLog(data: any): void {
    try {
      // Handle execution-specific log updates
      if (data && data.executionId) {
        const subscription = this.subscriptions.get(data.executionId);
        if (subscription) {
          subscription.callbacks.forEach(callback => {
            try {
              callback(data);
            } catch (error) {
              console.error('Error in execution log callback:', error);
            }
          });
        }
      }
    } catch (error) {
      console.error('Error handling execution log:', error);
    }
  }

  private handleError(error: Error): void {
    console.error('Logs WebSocket error:', error);
    this.updateConnectionState(ConnectionState.ERROR);
  }

  private handleClose(): void {
    if (this.socket) this.socket.userId = undefined;
    // socket.io retries on this same socket and re-emits 'connected'; calling
    // connect() from here raced its internal loop into a second live socket.
    this.updateConnectionState(ConnectionState.DISCONNECTED);
  }

  /** Detach and close the current socket so connect() can build a fresh one. */
  private teardownSocket(): void {
    if (!this.socket) return;
    this.socket.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;

    this.reconnectTimeout = setTimeout(() => {
      this.teardownSocket();
      this.connect();
    }, delay);
  }

  private updateConnectionState(state: ConnectionState): void {
    this.connectionState = state;
    this.stateCallbacks.forEach(callback => {
      try {
        callback(state);
      } catch (error) {
        console.error('Error in state callback:', error);
      }
    });
  }
}

// Create singleton instance
const logsWebSocketService = new LogsWebSocketService();
export default logsWebSocketService;

